import { APP_ASSETS } from "@/types/supported-venue";
import { isUseLocalModel } from "@/utils/link-detection";
import * as turf from "@turf/turf";

import { XiquMapInformation } from "@/utils/mapInformation/XiquMapInformation";
import { AdmiraltyStationMapInformation } from "@/utils/mapInformation/AdmiraltyStationMapInformation";
import { FoodExpo2026MapInformation } from "@/utils/mapInformation/FoodExpo2026MapInformation";
import { HkiaMapInformation } from "@/utils/mapInformation/HkiaMapInformation";
import { HkustMapInformation }from "@/utils/mapInformation/HkustMapInformation";
import { MplusMapInformation } from "@/utils/mapInformation/MplusMapInformation";
import { HkpmMapInformation } from "@/utils/mapInformation/HkpmMapInformation";
import { WestKowloonStationMapInformation } from "@/utils/mapInformation/WestKowloonStationMapInformation";
import { ElementsMapInformation } from "@/utils/mapInformation/ElementsMapInformation";

interface ILatLngPair {
  lat: number;
  lng: number;
}
interface ISizePair {
  width: number;
  height: number;
}
interface MapData {
  id: string;
  name: string;
  level: number;
}

export interface MapInformation {
  alignmentSetting?: string;
  venue?: string;
  name?: string;
  shortName?: string;
  projectId?: number;
  venueId?: string;
  entry_mapId?: number;
  i18nVenue?: string;
  venueName?: string;
  venueName_zh?: string;
  avatar?: string;
  avatarIsFloating?: boolean;
  floorAlignment: IMapAlign[];
  modelConfig?: VenueModelConfig;
  mapData?: MapData[];
}

export interface IMapAlign {
  lcMapId?: string;
  id: string;
  ordinal: number;
  name: string;
  shortName?: string;
  imageUrl: string;
  pixInMeter: number;
  center: ILatLngPair;
  angle: number;
  scale: number;
  corners: {
    tl: ILatLngPair;
    tr: ILatLngPair;
    br: ILatLngPair;
    bl: ILatLngPair;
  };
  size: ISizePair;
  sizeInMeters: ISizePair;
  polygon?: any;
  lcBound?: any;
}

// export interface IMapAlign {
//   name: string;
//   projectId?: number;
//   venueId?: string;
//   entry_mapId?: number;
//   i18nVenue: string;
//   venueName?: string;
//   venueName_zh?: string;
//   floorAlignment: FloorAlignment[];
//   imageUrl?: string;
//   pixInMeter?: number;
//   center?: ILatLngPair;
//   angle?: number;
//   scale?: number;
//   corners?: {
//     tl: ILatLngPair;
//     tr: ILatLngPair;
//     br: ILatLngPair;
//     bl: ILatLngPair;
//   };
//   size?: ISizePair;
//   sizeInMeters?: ISizePair;
//   venue?: string;
//   polygon?: any;
//   mapData?: MapData[];
//   modelConfig?: VenueModelConfig;
// }

// export type FloorAlignment = {
//   lcMapId?: string;
//   id: string;
//   ordinal: number;
//   name: string;
//   shortName?: string;
//   imageUrl: string;
//   pixInMeter: number;
//   center: ILatLngPair;
//   angle: number;
//   scale: number;
//   corners: {
//     tl: ILatLngPair;
//     tr: ILatLngPair;
//     br: ILatLngPair;
//     bl: ILatLngPair;
//   };
//   size: ISizePair;
//   sizeInMeters: ISizePair;
// };

export type VenueModelConfig = {
  folderPath: string;
  defaultPosition: number[];
  defaultTarget?: number[];
  scaleFactor: number;
  moveSpeed: number;
  rotateSpeed: number;
  revealSpeed?: number;
  defaultFloorIndex?: number;
  defaultLcMapId?: string;
  topAltitude: number;
  downSpeed: number;
  mainSplat?: string;
  floorMesh?: string;
  floorMeshPosition?: number[];
  floor?: Array<VenueModelFloor>;
  mesh?: meshSetting;
  splat?: splatSetting;
};

export type VenueModelFloor = {
  lcMapId?: string;
  gisLevelId?: string;
  // index: number;
  name: string;
  shortName?: string;
  topViewModel?: string;
  yAxis?: number;
  lod?: string;
  displayLod?: string[];
  sog?: string;
  mesh?: MeshModel;
  altitude: number;
  cameras?: number[][];
  defaultPosition?: number[];
  defaultTarget?: number[];
  meshGround?: string;
  meshObstacle?: string;
  annotation?: string;
  isNotReady?: boolean;
};

export type splatSetting = {
  mode: string;
  singleLod?: string;
  floorLod?: floorLodSetting[];
};

export type floorLodSetting = {
  index: number;
  lod: string;
  yAxis: number;
  displayLod: number[];
};

export type meshSetting = {
  meshModels: MeshModel[];
};

export type MeshModel = {
  index: number;
  url: string;
  position: number[];
  rotationAngle: number[];
  scale: number[];
  color?: string;
  opacity?: number;
  rotateObjects?: string[];
};

export type AnnotationTagPositionType = {
  x: number;
  y: number;
  z: number;
};
export type AnnotationTagScreenPosType = {
  id: string;
  worldPos: pc.Vec3;
  screenPos: AnnotationTagPositionType;
};

export const AnnotationTagColorPool = [
  "#6aa3ff",
  "#ff8a5b",
  "#5be0a0",
  "#f4d35e",
  "#c084fc",
  "#ff6b9d",
  "#4dd0e1",
  "#f28b82",
  "#a8e063",
  "#ffb74d",
  "#7c9cff",
  "#ff5252",
  "#26c6da",
  "#9ccc65",
  "#ec407a",
  "#ffa726",
  "#ab47bc",
  "#66bb6a",
  "#42a5f5",
  "#ffca28",
  "#8d6e63",
  "#78909c",
  "#d4e157",
  "#ff7043",
  "#5c6bc0",
  "#29b6f6",
  "#ef5350",
  "#26a69a",
  "#ffee58",
  "#ba68c8",
];

export const calculateXy = (mapAlign: IMapAlign, lat: number, lng: number) => {
  const target = turf.point([lng, lat]);
  const coordTL = mapAlign["corners"]["tl"];
  const coordTR = mapAlign["corners"]["tr"];
  const pointTL = turf.point([coordTL["lng"], coordTL["lat"]]);
  const pointTR = turf.point([coordTR["lng"], coordTR["lat"]]);

  const distanceTlToTarget = turf.distance(pointTL, target, {
    units: "meters",
  });
  const bearingTlToTarget = turf.bearing(pointTL, target);
  const bearingTlToTr = turf.bearing(pointTL, pointTR);
  const pxDiagonal = Math.sqrt(
    mapAlign["size"]["width"] ** 2 + mapAlign["size"]["height"] ** 2,
  );
  const meterDiagonal = Math.sqrt(
    mapAlign["sizeInMeters"]["width"] ** 2 +
      mapAlign["sizeInMeters"]["height"] ** 2,
  );
  const pxInMeter = pxDiagonal / meterDiagonal;
  const pxDistance = pxInMeter * distanceTlToTarget;

  const bearingOffset = bearingTlToTarget - (bearingTlToTr - 90);
  const angleTlToTarget = bearingOffset * (Math.PI / 180);
  const xOffset = pxDistance * Math.sin(angleTlToTarget);
  const yOffset = pxDistance * Math.cos(angleTlToTarget);
  return { x: xOffset, y: -1 * yOffset };
};

export const calculateLatLng = (mapAlign: IMapAlign, x: number, y: number) => {
  const coordTL = mapAlign.corners.tl;
  const coordTR = mapAlign.corners.tr;

  const pointTL = turf.point([coordTL.lng, coordTL.lat]);
  const pointTR = turf.point([coordTR.lng, coordTR.lat]);

  const pxDiagonal = Math.sqrt(
    mapAlign.size.width ** 2 + mapAlign.size.height ** 2,
  );
  const meterDiagonal = Math.sqrt(
    mapAlign.sizeInMeters.width ** 2 + mapAlign.sizeInMeters.height ** 2,
  );
  const meterPerPx = meterDiagonal / pxDiagonal;

  const pxDistance = Math.sqrt(x * x + y * y);
  const distanceMeters = pxDistance * meterPerPx;

  const bearingTlToTr = turf.bearing(pointTL, pointTR);

  const angleTlToTarget = Math.atan2(x, -y); // matches your x/y convention
  const bearingTlToTarget =
    bearingTlToTr - 90 + (angleTlToTarget * 180) / Math.PI;

  const target = turf.destination(pointTL, distanceMeters, bearingTlToTarget, {
    units: "meters",
  });

  const [lng, lat] = target.geometry.coordinates;
  return { lat, lng };
};

const backupInfo = [
  { id: 3, name: "Art Park", name_zh: "藝術公園" },
  { id: 4, name: "Freespace", name_zh: "自由空間" },
  { id: 5, name: "Hong Kong Palace Museum", name_zh: "香港故宮文化博物館" },
  { id: 6, name: "M+", name_zh: "M+" },
  { id: 7, name: "Xiqu Centre", name_zh: "戲曲中心" },
];

// const ALL_MAP_ALIGN: IMapAlign[] = [
//     {"venue":"Xiqu","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/50beb999-6abd-47d1-960d-16d65aaf56ab/m2-1718339315327ZERALU.png","pixInMeter":19.397799726963633,"center":{"lat":22.30216765875302,"lng":114.16723631100545},"angle":356.1234534144261,"scale":51.55228702359372,"corners":{"tl":{"lat":22.30271961208479,"lng":114.1665529985258},"tr":{"lat":22.30280349838915,"lng":114.1678294698037},"br":{"lat":22.30161570542125,"lng":114.1679196234851},"bl":{"lat":22.30153181911689,"lng":114.1666431522071}},"size":{"width":2560,"height":2560},"sizeInMeters":{"width":131.6500605703039,"height":132.4019818772277}},
//     {"venue":"M+","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/adec446e-8ad3-49a4-bd90-fed5f442ebbd/m2-1718707281081CEFOFU.png","pixInMeter":20.747134679934007,"center":{"lat":22.300853443913553,"lng":114.15912420169451},"angle":356.2152582177846,"scale":48.19950495769161,"corners":{"tl":{"lat":22.301680646033,"lng":114.1581034463103},"tr":{"lat":22.30180328806959,"lng":114.1600131494735},"br":{"lat":22.30002624179411,"lng":114.1601449570787},"bl":{"lat":22.29990359975752,"lng":114.1582352539155}},"size":{"width":4096,"height":4096},"sizeInMeters":{"width":196.93818859815383,"height":198.06353462167613}},
//     {"venue":"HKPM","name":"L0","imageUrl":"https://locaite-dev.collectiv.co/assets/c2461d0c-c362-4236-aaa8-0ce4e3f5b046/m1-1718702095995REHAYI.png","pixInMeter":20.8194792310068,"center":{"lat":22.301620666732227,"lng":114.1554200710521},"angle":358.97274743891023,"scale":48.03202207611783,"corners":{"tl":{"lat":22.30248931633708,"lng":114.1544468985085},"tr":{"lat":22.30252622803213,"lng":114.1563535646936},"br":{"lat":22.30075201712738,"lng":114.1563932435957},"bl":{"lat":22.30071510543232,"lng":114.1544865774106}},"size":{"width":4096,"height":4096},"sizeInMeters":{"width":196.1948851364027,"height":197.32575120636898}}
// ];

//splat mode : "singleLod" or "floorLod"
//Extra manuel custom data
const ALL_MAP_ALIGN: MapInformation[] = [
  XiquMapInformation,
  FoodExpo2026MapInformation,
  AdmiraltyStationMapInformation,
  WestKowloonStationMapInformation,
  HkiaMapInformation,
  HkustMapInformation,
  MplusMapInformation,
  HkpmMapInformation,
  ElementsMapInformation,
];

export const getVenueMapInformationByVenueId = (
  venueId: string,
): MapInformation => {
  const mapAlign = ALL_MAP_ALIGN.find((map) => map.venueId === venueId) || null;
  if (mapAlign) {
    return mapAlign;
  } else {
    return ALL_MAP_ALIGN[0];
  }
};

// export const findMapAlignWithGisLevelId = (
//   mapAligns: IMapAlign[],
//   gisLevelId: string,
//   point: any,
// ) => {
//   const matched = mapAligns.filter((m) => m.id === gisLevelId);
//   if (matched.length === 0) return undefined;
//   if (matched.length === 1) return matched[0];
//   const posMatched = matched.find((m) => {
//     return turf.booleanPointInPolygon(
//       point,
//       turf.polygon([
//         [
//           [m.corners.tl.lng, m.corners.tl.lat],
//           [m.corners.tr.lng, m.corners.tr.lat],
//           [m.corners.br.lng, m.corners.br.lat],
//           [m.corners.bl.lng, m.corners.bl.lat],
//           [m.corners.tl.lng, m.corners.tl.lat],
//         ],
//       ]),
//     );
//   });
//   return posMatched;
// };

export const findMapAlignWithGisLevelId = (
  mapAligns: IMapAlign[],
  gisLevelId: string,
  point: any
) => {
  const matched = mapAligns.filter((m) => m.id === gisLevelId);
  if (matched.length === 0) return undefined;
  if (matched.length === 1) return matched[0];
  const posMatched = matched.find((m) => {
    return turf.booleanPointInPolygon(
      point,
      turf.polygon(
        m.lcBound
          ? [m.lcBound]
          : [
              [
                [m.corners.tl.lng, m.corners.tl.lat],
                [m.corners.tr.lng, m.corners.tr.lat],
                [m.corners.br.lng, m.corners.br.lat],
                [m.corners.bl.lng, m.corners.bl.lat],
                [m.corners.tl.lng, m.corners.tl.lat],
              ],
            ],
      ),
    );
  });
  return posMatched ?? matched[0];
};

export const findMapAlignWithLcMapId = (
  mapAligns: IMapAlign[],
  lcMapId: string,
) => {
  const mapAlign = mapAligns.find((map) => map.lcMapId === lcMapId);
  return mapAlign ?? null;
};

export const getModelConfigMapByLcMapId = (
  venueModelConfig: VenueModelConfig | null,
  lcMapId: string,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return 0;
  }
  const mapData = venueModelConfig.floor.find((map) => map.lcMapId === lcMapId);
  return mapData ?? null;
};

const _findWkcdMapAlign = (point: any) => {
  for (let i = 0; i < ALL_MAP_ALIGN.length; i++) {
    if (!ALL_MAP_ALIGN[i].floorAlignment[0].polygon) {
      ALL_MAP_ALIGN[i].floorAlignment[0].polygon = turf.polygon([
        [
          [
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tl.lng,
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tl.lat,
          ],
          [
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tr.lng,
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tr.lat,
          ],
          [
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.br.lng,
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.br.lat,
          ],
          [
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.bl.lng,
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.bl.lat,
          ],
          [
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tl.lng,
            ALL_MAP_ALIGN[i].floorAlignment[0].corners.tl.lat,
          ],
        ],
      ]);
    }
    if (
      turf.booleanPointInPolygon(
        point,
        ALL_MAP_ALIGN[i].floorAlignment[0].polygon,
      )
    ) {
      return ALL_MAP_ALIGN[i];
    }
  }
  return undefined;
};

// export const calculateWkcdXy = (lat: number, lng: number) => {
//   const mAlign = _findWkcdMapAlign(turf.point([lng, lat]));
//   if (!mAlign) {
//     console.error("Failed to resolve WKCD venue from lat lng.");
//     return undefined;
//   }
//   const result = calculateXy(mAlign, lat, lng);
//   return { venue: mAlign.venue, ...result };
// };

export const getVenue = (lat: number, lng: number) => {
  return _findWkcdMapAlign(turf.point([lng, lat]));
};

export const getVenueShortNameById = (venueId: string) => {
  return ALL_MAP_ALIGN.find((map) => map.venueId === venueId)?.venue || "";
};

export const getVenueByShortName = (venue: string) => {
  return ALL_MAP_ALIGN.find(
    (map) => map.venue?.toLowerCase() === venue.toLowerCase(),
  );
};

export const getVenue18nName = (projectId: number) => {
  return (
    ALL_MAP_ALIGN.find((map) => map.projectId === projectId)?.i18nVenue || ""
  );
};

export const getMapAlignById = (venueId: string): MapInformation => {
  const mapAlign = ALL_MAP_ALIGN.find((map) => map.venueId === venueId) || null;
  if (mapAlign) {
    return mapAlign;
  } else {
    return ALL_MAP_ALIGN[0];
  }
};

export const getAllFloorMapAlignByVenueId = (venueId: string): IMapAlign[] => {
  const mapAlign = ALL_MAP_ALIGN.find((map) => map.venueId === venueId) || null;
  if (mapAlign) {
    return mapAlign.floorAlignment;
  } else {
    return ALL_MAP_ALIGN[0].floorAlignment;
  }
};

export const getFloorMapAlignByLevelIndex = (
  levelIndex: number,
  allFloorMapAlign: IMapAlign[],
): IMapAlign => {
  const alignment = allFloorMapAlign.find(
    (item) => item.ordinal === levelIndex,
  );
  if (alignment) {
    return alignment;
  } else {
    return allFloorMapAlign[0];
  }
};

// const venueMapIds = [
//   {
//     projectId: "149", //xiqu
//     mapIds: [
//       { id: 416, name: "B2", level: -2 },
//       { id: 417, name: "LG", level: -1 },
//       { id: 418, name: "G", level: 0 },
//       { id: 419, name: "L1", level: 1 },
//       { id: 420, name: "L2", level: 2 },
//     ],
//   },
//   {
//     projectId: "150", // WestKowloon Station
//     mapIds: [
//       { id: 426, name: "L1", level: 2 },
//       { id: 427, name: "GFM", level: 1 },
//       { id: 428, name: "G", level: 0 },
//       { id: 429, name: "B1M", level: -1 },
//       { id: 430, name: "B1", level: -2 },
//       { id: 431, name: "B2M", level: -3 },
//       { id: 432, name: "B2", level: -4 },
//     ],
//   },
// ];

// export const getVenueMapId = (venueId: string, level: string) => {
//   const venue = venueMapIds.find((v) => v.projectId === venueId);
//   const map = venue?.mapIds.find((map) => map.level.toString() === level);
//   return map?.id.toString();
// };

export const isVenueSupported = (venueCode: string) => {
  const venue = ALL_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  return venue != null;
};

export const getVenueMapId = (
  venueId: string,
  level: string,
): string | undefined => {
  const venue = ALL_MAP_ALIGN.find((v) => v.venueId === venueId);
  const map = venue?.mapData?.find((map) => map.level.toString() === level);
  return map?.id.toString();
};

export const getMapSettingByVenueId = (
  venueId: string,
): MapInformation | null => {
  const mapSetting = ALL_MAP_ALIGN.find((v) => v.venueId === venueId);
  return mapSetting ?? null;
};

export const getMapSettingByVenueCode = (
  venueCode: string,
): MapInformation | null => {
  const mapSetting = ALL_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  return mapSetting ?? null;
};

export const getVenueModelConfigByVenueId = (
  venueId: string,
): VenueModelConfig | null => {
  // if (venueCode in venueModelConfig) {
  //   return venueModelConfig[venueCode];
  // } else {
  //   return venueModelConfig["xiqu"];
  // }
  const venue = ALL_MAP_ALIGN.find((v) => v.venueId === venueId);
  const modelConfig = venue?.modelConfig;
  return modelConfig ?? null;
};

export const getVenueModelConfigByVenueCode = (
  venueCode: string,
): VenueModelConfig | null => {
  const venue = ALL_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  const modelConfig = venue?.modelConfig;
  return modelConfig ?? null;
};

export const getVenueIdByVenueCode = (venueCode: string): string => {
  const venue = ALL_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  return venue?.venueId ?? "";
};

export const getVenueNameByVenueCode = (venueCode: string): string => {
  const venue = ALL_MAP_ALIGN.find(
    (v) => v?.venue?.toLowerCase() === venueCode.toLowerCase(),
  );
  const venueName = venue?.venueName;
  return venueName ?? "";
};

export const getBaseUrl = () => {
  const BASE_URL = isUseLocalModel() ? "/" : `${APP_ASSETS}/`;
  return BASE_URL;
};

export const getAltitudeByFloorName = (
  venueModelConfig: VenueModelConfig | null,
  floorName: string,
) => {
  if (venueModelConfig == null || venueModelConfig.floor == null) {
    return "";
  }
  const floorData = venueModelConfig.floor.find(
    (floor) => floor.name === floorName,
  );
  return floorData?.altitude ?? 0;
};

export const getFloorSizeByFloorIndex = (
  floorAlignments: IMapAlign[],
  floorIndex: number,
): ISizePair => {
  const floorAlignment =
    floorAlignments.find((map) => map.ordinal == floorIndex) || null;
  if (floorAlignment) {
    return floorAlignment.size;
  } else {
    const defaultSize: ISizePair = {
      width: 1024,
      height: 1024,
    };
    return defaultSize;
  }
};

export const getFloorIndexByFloorId = (
  floorAlignments: IMapAlign[],
  floorId: string,
): number => {
  const floorAlignment =
    floorAlignments.find((map) => map.id == floorId) || null;
  if (floorAlignment) {
    return floorAlignment.ordinal;
  } else {
    return 0;
  }
};
